<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import {
    CalendarRange,
    Check,
    ChevronDown,
    Plus,
    RefreshCw,
    Search,
    Tag,
    Users,
    X,
  } from 'lucide-svelte';
  import {
    type CalendarDate,
    type DateValue,
    getLocalTimeZone,
    parseDate,
  } from '@internationalized/date';
  import { querystring, replace } from 'svelte-spa-router';
  import {
    type AccountKindFilter,
    type ParityStatus,
    type PortfolioStats,
    type SessionSummary,
    type SessionsQueryParams,
    type StatusFilter,
    fetchParityStatuses,
    fetchPortfolioStats,
    fetchRenkoSessionIds,
    fetchSessionFilterOptions,
    fetchSessionsPaginated,
  } from '$lib/api/sessions';
  import { Badge } from '$lib/components/ui/badge';
  import { Button } from '$lib/components/ui/button';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
  } from '$lib/components/ui/dropdown-menu';
  import { Input } from '$lib/components/ui/input';
  import { RangeCalendar } from '$lib/components/ui/range-calendar';
  import { Skeleton } from '$lib/components/ui/skeleton';
  import { type ScheduledTask, debounce, scheduleAlignedRefresh } from '$lib/utils/schedule';
  import AppShell from './AppShell.svelte';
  import Pagination from './Pagination.svelte';
  import PortfolioOverview from './PortfolioOverview.svelte';
  import SessionsTable, { type SortColumn, type SortDir } from './SessionsTable.svelte';

  // ---------- Estado server-side ----------
  let pageSessions = $state<SessionSummary[]>([]); // pagina actual
  let allSessionIds = $state<string[]>([]); // ids del set filtrado completo (para portfolio)
  let renkoSessionIds = $state<Set<string>>(new Set());
  let parityStatuses = $state<Map<string, ParityStatus>>(new Map());
  let portfolioStats = $state<PortfolioStats | null>(null);
  let portfolioStatsLoading = $state(false);
  let loading = $state(true);
  let refreshing = $state(false);
  let error = $state<string | null>(null);
  let lastFetch = $state<Date | null>(null);

  // Counts por kind: para los segments del control. Necesitamos hits del backend
  // sin filtrar por kind. Hacemos un mini-fetch al boot que solo pide totales.
  let kindCounts = $state({ all: 0, live: 0, sim: 0, demo: 0, unknown: 0 });
  let statusCounts = $state({ all: 0, live: 0, closed: 0, active: 0 });

  // ---------- Filtros + sort + pagination ----------
  let kindFilter = $state<AccountKindFilter>('live');
  let searchInput = $state('');
  let search = $state(''); // valor con debounce aplicado
  let statusFilter = $state<StatusFilter>('all');
  let sortBy = $state<SortColumn>('startTime');
  let sortDir = $state<SortDir>('desc');
  let page = $state(1);
  let pageSize = $state(50);
  let total = $state(0);
  let pages = $state(0);

  // ---------- Nuevos filtros (port v1 dropdowns) ----------
  // Date preset: 'all' | 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'custom'.
  // Para 'custom' se usan dateFrom/dateTo. Para los presets, computamos el rango al vuelo en ET.
  type DatePreset =
    | 'all'
    | 'today'
    | 'yesterday'
    | 'thisWeek'
    | 'lastWeek'
    | 'thisMonth'
    | 'custom';
  let datePreset = $state<DatePreset>('all');
  let customDateFrom = $state(''); // YYYY-MM-DD
  let customDateTo = $state(''); // YYYY-MM-DD

  // Multi-select Sets. Vacío = "todos" (sin filtro). Con valores = filtrar a esos.
  let selectedAccounts = $state<Set<string>>(new Set());
  let selectedTitles = $state<Set<string>>(new Set());

  // Opciones disponibles (fetched al mount + cada vez que cambia kindFilter).
  let availableAccounts = $state<string[]>([]);
  let availableTitles = $state<string[]>([]);

  // Apertura de dropdowns (controlado para limpiar UI al click outside).
  let accountDropdownOpen = $state(false);
  let titleDropdownOpen = $state(false);
  let dateDropdownOpen = $state(false);

  // Toggle "More filters": cuando true muestra Title dropdown. Off por default
  // (decisión operador 2026-05-26 — Titles raramente útil).
  let showMoreFilters = $state(false);

  // Helpers de conversión string YYYY-MM-DD ↔ DateValue para bind del calendar.
  function ymdToDate(s: string): CalendarDate | undefined {
    if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined;
    try {
      return parseDate(s);
    } catch {
      return undefined;
    }
  }
  function dateToYmd(d: DateValue | undefined): string {
    if (!d) return '';
    return d.toString(); // CalendarDate.toString() devuelve 'YYYY-MM-DD'
  }

  // Range value para el RangeCalendar — derivado de customDateFrom/To.
  let customRange = $state<{ start: DateValue | undefined; end: DateValue | undefined }>({
    start: undefined,
    end: undefined,
  });
  // Cuando RangeCalendar cambia, sincronizamos los strings + fetch.
  $effect(() => {
    const newFrom = dateToYmd(customRange.start);
    const newTo = dateToYmd(customRange.end);
    if (newFrom !== customDateFrom || newTo !== customDateTo) {
      customDateFrom = newFrom;
      customDateTo = newTo;
      if (newFrom || newTo) {
        if (datePreset !== 'custom') datePreset = 'custom';
        page = 1;
        persistFiltersToUrl();
      }
    }
  });

  const debouncedSetSearch = debounce((v: string) => {
    search = v;
    page = 1; // reset al filtrar
    persistFiltersToUrl();
  }, 250);

  // ----- helpers de fecha (presets en ET wall-clock) -----
  // Devuelven { from, to } como ISO YYYY-MM-DD del trading day ET. Si preset
  // es 'all', ambos undefined. 'custom' usa los inputs del operador.
  function etTodayYmd(): string {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  }
  function shiftEtYmd(ymd: string, days: number): string {
    // ymd es 'YYYY-MM-DD' ET. Sumar/restar dias preservando ET.
    const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + days);
    return dt.toISOString().slice(0, 10);
  }
  function etDayOfWeek(ymd: string): number {
    // 0 = Sunday, 6 = Saturday. Computado desde el Date UTC del YMD.
    const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  }
  function presetRange(p: DatePreset): { from?: string; to?: string } {
    if (p === 'all') return {};
    if (p === 'custom')
      return { from: customDateFrom || undefined, to: customDateTo || undefined };
    const today = etTodayYmd();
    if (p === 'today') return { from: today, to: today };
    if (p === 'yesterday') {
      const y = shiftEtYmd(today, -1);
      return { from: y, to: y };
    }
    if (p === 'thisWeek') {
      // Semana lunes a domingo (convencion trading; v1 usa eso).
      const dow = etDayOfWeek(today);
      const daysFromMon = (dow + 6) % 7; // 0=Sun→6, 1=Mon→0, ...
      const from = shiftEtYmd(today, -daysFromMon);
      return { from, to: today };
    }
    if (p === 'lastWeek') {
      const dow = etDayOfWeek(today);
      const daysFromMon = (dow + 6) % 7;
      const thisMon = shiftEtYmd(today, -daysFromMon);
      const lastMon = shiftEtYmd(thisMon, -7);
      const lastSun = shiftEtYmd(thisMon, -1);
      return { from: lastMon, to: lastSun };
    }
    if (p === 'thisMonth') {
      const [y, m] = today.split('-');
      const from = `${y}-${m}-01`;
      return { from, to: today };
    }
    return {};
  }

  const datePresetLabels: Record<DatePreset, string> = {
    all: 'All Dates',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    lastWeek: 'Last Week',
    thisMonth: 'This Month',
    custom: 'Custom Range',
  };

  // Label compacto para el trigger del dropdown.
  let dateTriggerLabel = $derived.by((): string => {
    if (datePreset === 'all') return 'All Dates';
    if (datePreset === 'custom') {
      const f = customDateFrom || '...';
      const t = customDateTo || '...';
      return `${f} → ${t}`;
    }
    return datePresetLabels[datePreset];
  });

  // ---------- URL persist ----------
  function persistFiltersToUrl(): void {
    const params = new URLSearchParams();
    if (kindFilter !== 'live') params.set('kind', kindFilter);
    if (search) params.set('q', search);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (sortBy !== 'startTime') params.set('sort', sortBy);
    if (sortDir !== 'desc') params.set('dir', sortDir);
    if (page !== 1) params.set('page', String(page));
    if (datePreset !== 'all') params.set('datePreset', datePreset);
    if (datePreset === 'custom') {
      if (customDateFrom) params.set('dateFrom', customDateFrom);
      if (customDateTo) params.set('dateTo', customDateTo);
    }
    if (selectedAccounts.size > 0) params.set('accounts', [...selectedAccounts].join(','));
    if (selectedTitles.size > 0) params.set('titles', [...selectedTitles].join(','));
    const qs = params.toString();
    replace(`/sessions${qs ? `?${qs}` : ''}`);
  }

  function restoreFiltersFromUrl(qs: string): void {
    const params = new URLSearchParams(qs);
    const kind = params.get('kind');
    if (
      kind === 'all' ||
      kind === 'live' ||
      kind === 'sim' ||
      kind === 'demo' ||
      kind === 'unknown'
    ) {
      kindFilter = kind;
    }
    const q = params.get('q') ?? '';
    searchInput = q;
    search = q;
    const st = params.get('status');
    if (st === 'live' || st === 'closed' || st === 'active') statusFilter = st;
    const sb = params.get('sort');
    if (
      sb === 'sessionId' ||
      sb === 'startTime' ||
      sb === 'symbol' ||
      sb === 'accountName' ||
      sb === 'operatorId' ||
      sb === 'totalPnL' ||
      sb === 'tradeCount' ||
      sb === 'winRate' ||
      sb === 'profitFactor'
    ) {
      sortBy = sb;
    }
    const dir = params.get('dir');
    if (dir === 'asc' || dir === 'desc') sortDir = dir;
    const p = Number(params.get('page'));
    if (Number.isFinite(p) && p >= 1) page = Math.floor(p);
    const dp = params.get('datePreset');
    if (
      dp === 'today' ||
      dp === 'yesterday' ||
      dp === 'thisWeek' ||
      dp === 'lastWeek' ||
      dp === 'thisMonth' ||
      dp === 'custom'
    ) {
      datePreset = dp;
    }
    const df = params.get('dateFrom');
    if (df && /^\d{4}-\d{2}-\d{2}$/.test(df)) customDateFrom = df;
    const dt = params.get('dateTo');
    if (dt && /^\d{4}-\d{2}-\d{2}$/.test(dt)) customDateTo = dt;
    // Sincronizar customRange con los strings al restaurar de URL.
    if (customDateFrom || customDateTo) {
      customRange = {
        start: ymdToDate(customDateFrom),
        end: ymdToDate(customDateTo),
      };
    }
    const accs = params.get('accounts');
    if (accs) selectedAccounts = new Set(accs.split(',').filter(Boolean));
    const titsParam = params.get('titles');
    if (titsParam) selectedTitles = new Set(titsParam.split(',').filter(Boolean));
  }

  // ---------- fetch principal ----------
  let scheduled: ScheduledTask | null = null;
  let lastFetchSignature = '';

  function buildParams(): SessionsQueryParams {
    const range = presetRange(datePreset);
    return {
      page,
      pageSize,
      sort: sortBy,
      dir: sortDir,
      kind: kindFilter,
      status: statusFilter,
      q: search || undefined,
      dateFrom: range.from,
      dateTo: range.to,
      accounts: selectedAccounts.size > 0 ? [...selectedAccounts] : undefined,
      titles: selectedTitles.size > 0 ? [...selectedTitles] : undefined,
    };
  }

  async function fetchPage(silent = false): Promise<void> {
    if (!silent) refreshing = true;
    const params = buildParams();
    try {
      const [pageRes, renkoIds, parity] = await Promise.allSettled([
        fetchSessionsPaginated(params),
        // Renko + parity son livianos y los seguimos cargando en paralelo;
        // los re-fetcheamos al refresh principal para que no se quedan stale.
        fetchRenkoSessionIds(),
        fetchParityStatuses(),
      ]);

      if (pageRes.status === 'fulfilled') {
        const res = pageRes.value;
        pageSessions = res.sessions;
        total = res.total;
        pages = res.pages;
        allSessionIds = res.allSessionIds;
        // Si page > pages (ej: filtraste y la pagina actual ya no existe),
        // resetear y re-fetchear pagina 1.
        if (page > res.pages && res.pages > 0) {
          page = 1;
          persistFiltersToUrl();
          await fetchPage(true);
          return;
        }
        error = null;
      } else {
        console.error('[sessions] fetchSessionsPaginated fallo', pageRes.reason);
        error = pageRes.reason instanceof Error ? pageRes.reason.message : 'Unknown error';
      }

      if (renkoIds.status === 'fulfilled') {
        renkoSessionIds = new Set(renkoIds.value);
      }
      if (parity.status === 'fulfilled') {
        parityStatuses = new Map(parity.value.map((p) => [p.sessionId, p.status]));
      }

      lastFetch = new Date();
    } finally {
      refreshing = false;
      loading = false;
    }
  }

  // Counts agregados (para badges de cada segment) - 1 sola request al boot
  // sin filtros para tener los totales reales por kind/status.
  async function fetchCounts(): Promise<void> {
    try {
      // 4 mini-requests en paralelo, cada uno con pageSize=1 para minimizar
      // payload (solo necesitamos `total`). Backend no expone count-only;
      // workaround simple hasta que se haga.
      const [all, live, sim, demo] = await Promise.all([
        fetchSessionsPaginated({ pageSize: 1 }),
        fetchSessionsPaginated({ kind: 'live', pageSize: 1 }),
        fetchSessionsPaginated({ kind: 'sim', pageSize: 1 }),
        fetchSessionsPaginated({ kind: 'demo', pageSize: 1 }),
      ]);
      kindCounts = {
        all: all.total,
        live: live.total,
        sim: sim.total,
        demo: demo.total,
        unknown: Math.max(0, all.total - live.total - sim.total - demo.total),
      };
      // Para los counts de status, usamos el set ya filtrado por kind actual
      // (asi reflejan lo que el operador esta viendo). Sin status filter.
      const [allStatus, liveOnly, closedOnly, activeOnly] = await Promise.all([
        fetchSessionsPaginated({ kind: kindFilter, pageSize: 1 }),
        fetchSessionsPaginated({ kind: kindFilter, status: 'live', pageSize: 1 }),
        fetchSessionsPaginated({ kind: kindFilter, status: 'closed', pageSize: 1 }),
        fetchSessionsPaginated({ kind: kindFilter, status: 'active', pageSize: 1 }),
      ]);
      statusCounts = {
        all: allStatus.total,
        live: liveOnly.total,
        closed: closedOnly.total,
        active: activeOnly.total,
      };
    } catch (err) {
      console.error('[sessions] fetchCounts fallo', err);
    }
  }

  // ---------- portfolio stats ----------
  // Re-fetcha cuando cambia allSessionIds (set filtrado completo).
  let lastStatsKey = '';
  const debouncedFetchStats = debounce(async (csv: string) => {
    if (csv === lastStatsKey) return;
    lastStatsKey = csv;
    portfolioStatsLoading = true;
    try {
      const ids = csv ? csv.split(',') : undefined;
      portfolioStats = await fetchPortfolioStats(ids);
    } catch (err) {
      console.error('[sessions] fetchPortfolioStats fallo', err);
    } finally {
      portfolioStatsLoading = false;
    }
  }, 300);

  let allSessionIdsKey = $derived([...allSessionIds].sort().join(','));

  $effect(() => {
    if (loading) return;
    debouncedFetchStats(allSessionIdsKey);
  });

  // ---------- handlers ----------
  function onSortChange(col: SortColumn): void {
    if (sortBy === col) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      sortBy = col;
      sortDir = col === 'totalPnL' || col === 'startTime' ? 'desc' : 'asc';
    }
    page = 1;
    persistFiltersToUrl();
    fetchPage();
  }

  function onSearchInput(e: Event): void {
    const v = (e.target as HTMLInputElement).value;
    searchInput = v;
    debouncedSetSearch(v);
  }

  function setStatus(s: StatusFilter): void {
    statusFilter = s;
    page = 1;
    persistFiltersToUrl();
    fetchPage();
  }

  function setKind(k: AccountKindFilter): void {
    kindFilter = k;
    page = 1;
    persistFiltersToUrl();
    fetchPage();
    // Counts de status dependen del kind, los recalculamos.
    fetchCounts();
  }

  function setPage(p: number): void {
    page = p;
    persistFiltersToUrl();
    fetchPage();
    // Scroll al top de la tabla para feedback visual de cambio de pagina.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Search effect: cuando cambia cualquier filtro server-side, disparamos fetch.
  $effect(() => {
    if (loading) return;
    const accKey = [...selectedAccounts].sort().join(',');
    const titKey = [...selectedTitles].sort().join(',');
    const sig = `${kindFilter}|${statusFilter}|${sortBy}|${sortDir}|${page}|${pageSize}|${search}|${datePreset}|${customDateFrom}|${customDateTo}|${accKey}|${titKey}`;
    if (sig === lastFetchSignature) return;
    lastFetchSignature = sig;
    fetchPage();
  });

  // ---------- fetch filter-options ----------
  async function fetchFilterOptions(): Promise<void> {
    try {
      const opts = await fetchSessionFilterOptions();
      availableAccounts = opts.accounts;
      availableTitles = opts.titles;
    } catch (err) {
      console.error('[sessions] fetchSessionFilterOptions fallo', err);
    }
  }

  onMount(async () => {
    restoreFiltersFromUrl($querystring ?? '');
    await Promise.all([fetchPage(), fetchCounts(), fetchFilterOptions()]);
    scheduled = scheduleAlignedRefresh(async () => {
      await fetchPage(true);
      await fetchCounts();
    }, 5);
  });

  onDestroy(() => {
    scheduled?.cancel();
  });

  function fmtLastFetch(d: Date | null): string {
    if (!d) return '—';
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  // ---------- segmented kind ----------
  let availableKinds = $derived.by((): AccountKindFilter[] => {
    const out: AccountKindFilter[] = ['all'];
    if (kindCounts.live > 0) out.push('live');
    if (kindCounts.sim > 0) out.push('sim');
    if (kindCounts.demo > 0) out.push('demo');
    if (kindCounts.unknown > 0) out.push('unknown');
    return out;
  });

  const kindLabels: Record<AccountKindFilter, string> = {
    all: 'All',
    live: 'Live',
    paper: 'Paper',
    sim: 'Sim',
    demo: 'Demo',
    unknown: 'Other',
  };

  // ---------- Handlers nuevos filtros ----------
  function setDatePreset(p: DatePreset): void {
    datePreset = p;
    if (p !== 'custom') {
      customDateFrom = '';
      customDateTo = '';
      customRange = { start: undefined, end: undefined };
    }
    page = 1;
    persistFiltersToUrl();
    // El $effect dispara el fetch automaticamente al detectar el cambio.
    // Cerramos el dropdown para feedback inmediato.
    if (p !== 'custom') dateDropdownOpen = false;
  }
  function toggleAccount(name: string): void {
    if (selectedAccounts.has(name)) selectedAccounts.delete(name);
    else selectedAccounts.add(name);
    selectedAccounts = new Set(selectedAccounts); // reactividad
    page = 1;
    persistFiltersToUrl();
  }
  function clearAccounts(): void {
    selectedAccounts = new Set();
    page = 1;
    persistFiltersToUrl();
  }
  function toggleTitle(name: string): void {
    if (selectedTitles.has(name)) selectedTitles.delete(name);
    else selectedTitles.add(name);
    selectedTitles = new Set(selectedTitles);
    page = 1;
    persistFiltersToUrl();
  }
  function clearTitles(): void {
    selectedTitles = new Set();
    page = 1;
    persistFiltersToUrl();
  }

  // Labels compactos para los triggers de los multi-selects.
  function fmtAccountLabel(v: string): string {
    return v === '__null__' ? '(no account)' : v;
  }
  let accountsTriggerLabel = $derived.by((): string => {
    if (selectedAccounts.size === 0) return 'All Accounts';
    if (selectedAccounts.size === 1) {
      const [v] = selectedAccounts;
      return v ? fmtAccountLabel(v) : 'All Accounts';
    }
    return `${selectedAccounts.size} accounts`;
  });
  let titlesTriggerLabel = $derived.by((): string => {
    if (selectedTitles.size === 0) return 'All Titles';
    if (selectedTitles.size === 1) {
      const [v] = selectedTitles;
      return v ?? 'All Titles';
    }
    return `${selectedTitles.size} titles`;
  });

  // Hay algun filtro nuevo activo? (para mostrar "Clear all filters")
  let hasAnyFilter = $derived(
    datePreset !== 'all' || selectedAccounts.size > 0 || selectedTitles.size > 0 || !!search,
  );
  function clearAllFilters(): void {
    datePreset = 'all';
    customDateFrom = '';
    customDateTo = '';
    customRange = { start: undefined, end: undefined };
    selectedAccounts = new Set();
    selectedTitles = new Set();
    searchInput = '';
    search = '';
    showMoreFilters = false;
    page = 1;
    persistFiltersToUrl();
  }
</script>

<AppShell>
  <section class="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
    <header class="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
      <div class="flex flex-col gap-1">
        <span class="text-eyebrow">Operations</span>
        <h1 class="text-display text-3xl md:text-4xl">Sessions</h1>
        <p class="text-muted-foreground mt-1 text-[13px]">
          Complete listing with live rollups. Auto-refresh every minute.
        </p>
      </div>
      <div class="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          disabled={refreshing}
          onclick={() => fetchPage()}
          title={lastFetch ? `Last refresh: ${fmtLastFetch(lastFetch)}` : undefined}
        >
          <RefreshCw class={['size-3.5', refreshing && 'animate-spin'].filter(Boolean).join(' ')} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>
    </header>

    {#if loading}
      <div class="space-y-4">
        <Skeleton class="h-32 w-full" />
        <Skeleton class="h-96 w-full" />
      </div>
    {:else if error}
      <div
        role="alert"
        class="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-4 py-3 text-sm"
      >
        Error loading sessions: {error}
      </div>
    {:else}
      <div class="space-y-6">
        <!-- Segmented control: Account kind -->
        <div class="flex items-center gap-2">
          <span class="text-eyebrow">
            Account
          </span>
          <div class="bg-muted/40 inline-flex rounded-md border p-0.5">
            {#each availableKinds as k (k)}
              {@const isActive = kindFilter === k}
              <button
                type="button"
                onclick={() => setKind(k)}
                class={[
                  'rounded-sm px-3 py-1 text-xs font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                {kindLabels[k]}
                <span class="ml-1 opacity-70 tabular-nums">{kindCounts[k as keyof typeof kindCounts]}</span>
              </button>
            {/each}
          </div>
          {#if kindFilter !== 'all' && kindFilter !== 'live'}
            <span class="text-warning text-xs">
              Showing fake/test money
            </span>
          {/if}
        </div>

        <PortfolioOverview
          sessions={pageSessions}
          stats={portfolioStats}
          statsLoading={portfolioStatsLoading}
        />

        <!-- Filter row: search + date + account + title + status (segmented).
             Densidad luxury: una sola row en md+, wrap en mobile. Sin gold/serif
             (controles, no synthesis). Triggers ghost cuando inactivos, outline
             con accent secondary cuando activos. -->
        <div class="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
          <!-- Search -->
          <div class="relative w-full max-w-xs md:flex-1">
            <Search class="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              type="search"
              placeholder="Search sessionId, symbol, account, title..."
              class="pl-9"
              value={searchInput}
              oninput={onSearchInput}
            />
          </div>

          <!-- Date dropdown (presets + custom range) -->
          <DropdownMenu bind:open={dateDropdownOpen}>
            <DropdownMenuTrigger>
              {#snippet child({ props })}
                <Button
                  {...props}
                  variant={datePreset === 'all' ? 'outline' : 'secondary'}
                  size="sm"
                  class="gap-1.5"
                >
                  <CalendarRange class="size-3.5" />
                  <span class="tabular-nums">{dateTriggerLabel}</span>
                  <ChevronDown class="size-3 opacity-60" />
                </Button>
              {/snippet}
            </DropdownMenuTrigger>
            <DropdownMenuContent class="flex w-auto gap-0 p-0">
              <!-- Columna izquierda: presets compactos -->
              <div class="border-border/40 w-44 border-r p-1">
                {#each ['all', 'today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth'] as p (p)}
                  {@const dp = p as DatePreset}
                  {@const isActive = datePreset === dp}
                  <button
                    type="button"
                    class={[
                      'hover:bg-muted/60 flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm transition-colors',
                      isActive && 'bg-muted/40 text-foreground',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onclick={() => setDatePreset(dp)}
                  >
                    <span>{datePresetLabels[dp]}</span>
                    {#if isActive}<Check class="text-primary size-3.5" />{/if}
                  </button>
                {/each}
                <div class="border-border/60 my-1 border-t"></div>
                <div class="text-muted-foreground/80 px-2 py-1 text-[10px] uppercase tracking-wider">
                  Custom range
                </div>
                <div class="px-2 pb-2 pt-1 text-[11px] tabular-nums">
                  {#if customDateFrom && customDateTo}
                    <div class="text-foreground font-mono">
                      {customDateFrom}
                    </div>
                    <div class="text-muted-foreground font-mono">→ {customDateTo}</div>
                  {:else if customDateFrom}
                    <div class="text-foreground font-mono">{customDateFrom}</div>
                    <div class="text-muted-foreground italic">pick end date</div>
                  {:else}
                    <div class="text-muted-foreground italic">pick a range →</div>
                  {/if}
                </div>
              </div>
              <!-- Columna derecha: calendar luxury (iOS-like) -->
              <RangeCalendar bind:value={customRange} />
            </DropdownMenuContent>
          </DropdownMenu>

          <!-- Account multi-select dropdown -->
          <DropdownMenu bind:open={accountDropdownOpen}>
            <DropdownMenuTrigger>
              {#snippet child({ props })}
                <Button
                  {...props}
                  variant={selectedAccounts.size > 0 ? 'secondary' : 'outline'}
                  size="sm"
                  class="gap-1.5"
                >
                  <Users class="size-3.5" />
                  <span>{accountsTriggerLabel}</span>
                  {#if selectedAccounts.size > 0}
                    <Badge variant="outline" class="ml-0.5 tabular-nums">
                      {selectedAccounts.size}
                    </Badge>
                  {/if}
                  <ChevronDown class="size-3 opacity-60" />
                </Button>
              {/snippet}
            </DropdownMenuTrigger>
            <DropdownMenuContent class="max-h-80 w-64 overflow-y-auto p-1">
              {#if availableAccounts.length === 0}
                <div class="text-muted-foreground px-2 py-3 text-center text-xs">
                  No accounts found
                </div>
              {:else}
                <div class="flex items-center justify-between px-2 py-1">
                  <span class="text-muted-foreground text-[10px] uppercase tracking-wider">
                    Accounts ({availableAccounts.length})
                  </span>
                  {#if selectedAccounts.size > 0}
                    <button
                      type="button"
                      class="text-muted-foreground hover:text-foreground text-[11px]"
                      onclick={clearAccounts}
                    >
                      Clear
                    </button>
                  {/if}
                </div>
                <div class="border-border/60 my-1 border-t"></div>
                {#each availableAccounts as acc (acc)}
                  {@const isChecked = selectedAccounts.has(acc)}
                  <button
                    type="button"
                    class="hover:bg-muted/60 flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm transition-colors"
                    onclick={() => toggleAccount(acc)}
                  >
                    <span class={acc === '__null__' ? 'text-muted-foreground italic' : ''}>
                      {fmtAccountLabel(acc)}
                    </span>
                    {#if isChecked}<Check class="text-primary size-3.5" />{/if}
                  </button>
                {/each}
              {/if}
            </DropdownMenuContent>
          </DropdownMenu>

          <!-- More filters toggle: Title dropdown queda oculto por default
               (decisión operador 2026-05-26 — raramente útil). -->
          {#if !showMoreFilters && selectedTitles.size === 0}
            <Button
              variant="ghost"
              size="sm"
              onclick={() => (showMoreFilters = true)}
              class="text-muted-foreground hover:text-foreground gap-1"
              title="Show Title filter"
            >
              <Plus class="size-3.5" />
              More filters
            </Button>
          {/if}

          <!-- Title multi-select dropdown -->
          {#if showMoreFilters || selectedTitles.size > 0}
          <DropdownMenu bind:open={titleDropdownOpen}>
            <DropdownMenuTrigger>
              {#snippet child({ props })}
                <Button
                  {...props}
                  variant={selectedTitles.size > 0 ? 'secondary' : 'outline'}
                  size="sm"
                  class="gap-1.5"
                >
                  <Tag class="size-3.5" />
                  <span>{titlesTriggerLabel}</span>
                  {#if selectedTitles.size > 0}
                    <Badge variant="outline" class="ml-0.5 tabular-nums">
                      {selectedTitles.size}
                    </Badge>
                  {/if}
                  <ChevronDown class="size-3 opacity-60" />
                </Button>
              {/snippet}
            </DropdownMenuTrigger>
            <DropdownMenuContent class="max-h-80 w-64 overflow-y-auto p-1">
              {#if availableTitles.length === 0}
                <div class="text-muted-foreground px-2 py-3 text-center text-xs">
                  No custom titles found
                </div>
              {:else}
                <div class="flex items-center justify-between px-2 py-1">
                  <span class="text-muted-foreground text-[10px] uppercase tracking-wider">
                    Titles ({availableTitles.length})
                  </span>
                  {#if selectedTitles.size > 0}
                    <button
                      type="button"
                      class="text-muted-foreground hover:text-foreground text-[11px]"
                      onclick={clearTitles}
                    >
                      Clear
                    </button>
                  {/if}
                </div>
                <div class="border-border/60 my-1 border-t"></div>
                {#each availableTitles as t (t)}
                  {@const isChecked = selectedTitles.has(t)}
                  <button
                    type="button"
                    class="hover:bg-muted/60 flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm transition-colors"
                    onclick={() => toggleTitle(t)}
                  >
                    <span class="truncate">{t}</span>
                    {#if isChecked}<Check class="text-primary size-3.5 shrink-0" />{/if}
                  </button>
                {/each}
              {/if}
            </DropdownMenuContent>
          </DropdownMenu>
          {/if}

          <!-- Clear all (visible solo si hay algo activo) -->
          {#if hasAnyFilter}
            <Button
              variant="ghost"
              size="sm"
              onclick={clearAllFilters}
              class="text-muted-foreground hover:text-foreground gap-1"
              title="Clear all filters"
            >
              <X class="size-3.5" />
              Clear
            </Button>
          {/if}

          <!-- Spacer push status segmented a la derecha en md+ -->
          <div class="hidden md:ml-auto md:block"></div>

          <!-- Status segmented (mantiene patrón existente) -->
          <div class="flex items-center gap-1">
            <Button
              variant={statusFilter === 'all' ? 'secondary' : 'ghost'}
              size="sm"
              onclick={() => setStatus('all')}
            >
              All <Badge variant="outline" class="ml-1.5 tabular-nums">{statusCounts.all}</Badge>
            </Button>
            <Button
              variant={statusFilter === 'active' ? 'secondary' : 'ghost'}
              size="sm"
              onclick={() => setStatus('active')}
              title="Sessions whose trading window includes now (ET)"
            >
              Active
              <Badge variant="outline" class="ml-1.5 tabular-nums">{statusCounts.active}</Badge>
            </Button>
            <Button
              variant={statusFilter === 'live' ? 'secondary' : 'ghost'}
              size="sm"
              onclick={() => setStatus('live')}
            >
              Live
              <Badge variant="outline" class="ml-1.5 tabular-nums">{statusCounts.live}</Badge>
            </Button>
            <Button
              variant={statusFilter === 'closed' ? 'secondary' : 'ghost'}
              size="sm"
              onclick={() => setStatus('closed')}
            >
              Closed
              <Badge variant="outline" class="ml-1.5 tabular-nums">{statusCounts.closed}</Badge>
            </Button>
          </div>
        </div>

        <div class="relative">
          {#if refreshing}
            <div
              class="bg-background/40 pointer-events-none absolute inset-0 z-10 backdrop-blur-[1px]"
              aria-hidden="true"
            ></div>
          {/if}
          <SessionsTable
            sessions={pageSessions}
            {renkoSessionIds}
            {parityStatuses}
            {sortBy}
            {sortDir}
            {onSortChange}
          />
        </div>

        <Pagination
          {page}
          {pages}
          {total}
          {pageSize}
          onPageChange={setPage}
          disabled={refreshing}
        />
      </div>
    {/if}
  </section>
</AppShell>
